/**
 * Wounds — persistent records with provenance, severity as qualitative levels,
 * and the per-location tables that turn a level into a consequence (AoD §4).
 *
 * Two rules carry the whole model:
 *
 * 1. **Levels are not hit points.** Twenty level-1 cuts make a miserable,
 *    hurting, still-standing farmer; one level-5 puncture to the chest kills.
 *    Nothing anywhere sums `level`. What *is* cumulative is blood (M1-04) and
 *    pain, and those are consequences of wounds, not a restatement of them.
 * 2. **The tables are content, not code.** This module owns the schema, the
 *    loader and the validator; `@annwn/content` owns every number and every
 *    line of prose. Balance passes and writing passes then touch no sim code,
 *    and a table with a hole in it fails a test rather than a duel.
 *
 * Provenance costs a few strings per wound and buys the entire payoff of AoD
 * §12.3: every scar knows its own story, years of played time later. It is
 * cheap now and unretrofittable later, which is the only reason it is here in
 * M1 rather than in the milestone that shows it off.
 */
import { clamp01 } from './body.js';
/**
 * Damage types for Annwn (AoD §4.4, re-cut for the dark ages).
 *
 * `bite` is not a fourth structure but an overlay on `puncture` — the same
 * ragged holes, a far worse infection risk, and its own narration. Ballistic
 * and splinter stayed behind with the navy; there are no guns here, and the
 * splinter shower has no analogue until something explodes.
 */
export const DAMAGE_TYPES = ['cut', 'puncture', 'blunt', 'bite', 'burn'];
export function isDamageType(value) {
    return typeof value === 'string' && DAMAGE_TYPES.includes(value);
}
export const WOUND_LEVELS = [0, 1, 2, 3, 4, 5];
export const MAX_WOUND_LEVEL = 5;
export function isWoundLevel(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 5;
}
/** Where a wound is in its life. `permanent` is the end of the line for a loss. */
export const WOUND_STATES = [
    'fresh',
    'treated',
    'healing',
    'infected',
    'healed',
    'permanent',
];
/**
 * Typed tags the wound system applies (AoD §4.5). Effects are how a table entry
 * reaches out and changes the world; a table cannot run code.
 *
 * - `sever` — the part comes off. Permanent.
 * - `break` — bone. Splintable, heals, heals *wrong* if never set.
 * - `lodge` — something stayed in the wound. Standing infection modifier until
 *   extracted; the surgery hook M4-05 pulls on.
 * - `blind` — an eye is gone.
 * - `kill` — fatal by location. The table says so; M1-04 carries it out.
 * - `knockdown` — the blow puts them down. Checked, not automatic (AoD §5.1).
 */
export const WOUND_EFFECTS = ['sever', 'break', 'lodge', 'blind', 'kill', 'knockdown'];
/** How a wound came to be. Widens as the world gains new ways to hurt people. */
export const WOUND_CAUSES = [
    'melee',
    'beast',
    'fall',
    'fire',
    'accident',
    'storm',
    'surgery',
];
/**
 * Tokens a narration template may use. Validated at load: a typo like
 * `{victm}` fails the content test rather than printing itself into a log.
 * The renderer that substitutes them lands with M1-06.
 */
export const NARRATION_TOKENS = [
    'victim',
    'attacker',
    'weapon',
    'part',
    'their',
    'them',
    /** The piece that turned the blow. Only a defence clause carries one. */
    'armour',
];
export class WoundTableError extends Error {
    constructor(message) {
        super(`wound table: ${message}`);
        this.name = 'WoundTableError';
    }
}
/** Minimum phrasings per cell, so a long fight does not repeat itself. */
export const MIN_NARRATION_TEMPLATES = 2;
/**
 * What a fragment left in the wound is worth, on top of the cell's own
 * modifier (AoD's ballistic lodging, re-cut for tusk splinters and — later —
 * arrowheads).
 *
 * Added into `wound.infectionMod` at infliction rather than derived from the
 * `lodge` effect on demand, so that M4-05's extraction has something to
 * subtract: effects are the immutable record of what the blow did, and pulling
 * the splinter out does not un-do the blow.
 */
export const LODGED_INFECTION_MOD = 0.1;
/**
 * Is there still something in this wound (M4-05)?
 *
 * The question extraction is offered on, and the one skilled examine answers.
 * A healed wound is not asked about: whatever the body did with the splinter
 * over the weeks it took to close, it is not a thing a knife can reach any
 * more, and offering the verb on a scar would be offering to re-open one.
 */
export function isLodged(wound) {
    return wound.effects.includes('lodge') && !wound.extracted && wound.state !== 'healed';
}
/**
 * The severity at or above which an open wound arrives dirty (M4-01).
 *
 * Mirrors `INFECTION_MIN_LEVEL` in `infection.ts` — below it a wound is a
 * scratch that does not roll and so has nothing for cleaning to do — but is
 * inlined rather than imported, because `infection.ts` already imports this
 * module and a cycle over one number is not a trade worth making.
 */
export const CONTAMINATION_MIN_LEVEL = 2;
/** The open wound types that carry filth in with them. A bruise does not. */
const DIRTY_TYPES = new Set(['cut', 'puncture', 'bite']);
const TOKEN_PATTERN = /\{([a-zA-Z]+)\}/g;
/**
 * A validated, flattened table. Overlays are materialised at construction, so
 * `resolve` is a map lookup and nothing at hit time knows `bite` was ever
 * borrowing from `puncture`.
 */
export class WoundTable {
    cells;
    constructor(cells) {
        this.cells = cells;
    }
    static from(source) {
        const cells = new Map();
        for (const type of Object.keys(source.entries)) {
            if (!isDamageType(type))
                throw new WoundTableError(`unknown damage type '${type}'`);
            const byZone = source.entries[type] ?? {};
            for (const zone of Object.keys(byZone)) {
                const ladder = byZone[zone];
                validateLadder(type, zone, ladder);
                cells.set(cellKey(type, zone), ladder);
            }
        }
        for (const overlay of source.overlays ?? []) {
            if (!isDamageType(overlay.type)) {
                throw new WoundTableError(`overlay has unknown type '${overlay.type}'`);
            }
            const base = source.entries[overlay.from];
            if (!base) {
                throw new WoundTableError(`overlay '${overlay.type}' borrows from '${overlay.from}', which the table does not define`);
            }
            for (const zone of Object.keys(base)) {
                const borrowed = base[zone];
                const swapped = overlay.narration?.[zone];
                const ladder = borrowed.map((entry, level) => ({
                    ...entry,
                    pain: entry.pain + (overlay.painMod ?? 0),
                    infectionMod: entry.infectionMod + overlay.infectionMod,
                    effects: overlay.effects ? dedupe([...entry.effects, ...overlay.effects]) : entry.effects,
                    narration: swapped?.[level] ?? entry.narration,
                }));
                validateLadder(overlay.type, zone, ladder);
                cells.set(cellKey(overlay.type, zone), ladder);
            }
        }
        if (cells.size === 0)
            throw new WoundTableError('is empty');
        return new WoundTable(cells);
    }
    /** Every `(type, zone)` pair the table defines. Drives the completeness test. */
    get coverage() {
        return [...this.cells.keys()].map((key) => {
            const [type, zone] = key.split('/');
            return { type, zone };
        });
    }
    has(type, zone) {
        return this.cells.has(cellKey(type, zone));
    }
    /**
     * The entry for a cell. Throws rather than returning a bland default: a
     * missing cell means an attack profile can reach a zone no writer covered,
     * and the only useful moment to learn that is the first time it happens.
     */
    resolve(type, zone, level) {
        const ladder = this.cells.get(cellKey(type, zone));
        if (!ladder)
            throw new WoundTableError(`no entries for ${type} to the ${zone}`);
        const entry = ladder[level];
        if (!entry)
            throw new WoundTableError(`no level ${level} entry for ${type} to the ${zone}`);
        return entry;
    }
}
function cellKey(type, zone) {
    return `${type}/${zone}`;
}
function dedupe(items) {
    return [...new Set(items)];
}
function validateLadder(type, zone, ladder) {
    const where = `${type}/${zone}`;
    if (!Array.isArray(ladder) || ladder.length !== WOUND_LEVELS.length) {
        throw new WoundTableError(`${where} needs exactly ${WOUND_LEVELS.length} levels`);
    }
    ladder.forEach((entry, level) => validateEntry(`${where} level ${level}`, entry));
}
function validateEntry(where, entry) {
    const nonNegative = (name) => {
        const value = entry[name];
        if (!Number.isFinite(value) || value < 0) {
            throw new WoundTableError(`${where}: ${String(name)} must be >= 0 (got ${value})`);
        }
    };
    nonNegative('bleedRate');
    nonNegative('shock');
    nonNegative('pain');
    nonNegative('infectionMod');
    if (!Number.isFinite(entry.impair) || entry.impair < 0 || entry.impair > 1) {
        throw new WoundTableError(`${where}: impair must be in [0, 1] (got ${entry.impair})`);
    }
    if (!Number.isFinite(entry.knockdownMod)) {
        throw new WoundTableError(`${where}: knockdownMod must be a number`);
    }
    for (const effect of entry.effects) {
        if (!WOUND_EFFECTS.includes(effect)) {
            throw new WoundTableError(`${where}: unknown effect '${effect}'`);
        }
    }
    if (entry.narration.length < MIN_NARRATION_TEMPLATES) {
        throw new WoundTableError(`${where}: needs at least ${MIN_NARRATION_TEMPLATES} narration templates (got ${entry.narration.length})`);
    }
    for (const template of entry.narration) {
        if (!template.trim())
            throw new WoundTableError(`${where}: has an empty narration template`);
        for (const match of template.matchAll(TOKEN_PATTERN)) {
            const token = match[1];
            if (!NARRATION_TOKENS.includes(token)) {
                throw new WoundTableError(`${where}: unknown narration token '{${token}}' in "${template}"`);
            }
        }
    }
}
/** Turn a resolved table entry into the record that goes on the body. */
export function createWound(options) {
    const { entry } = options;
    const effects = options.bonusEffects && options.bonusEffects.length > 0
        ? dedupe([...entry.effects, ...options.bonusEffects])
        : entry.effects;
    return {
        id: options.id,
        part: options.part,
        zone: options.zone,
        type: options.type,
        level: options.level,
        bleedRate: entry.bleedRate,
        pain: entry.pain,
        shock: entry.shock,
        impair: entry.impair,
        state: 'fresh',
        infectionClock: 0,
        // A fragment left in the wound keeps festering until somebody digs it out.
        infectionMod: entry.infectionMod + (entry.effects.includes('lodge') ? LODGED_INFECTION_MOD : 0),
        // An open wound of any consequence carries filth in with the blow; a bruise
        // and a scratch do not. Cleaning (M4-02) is what clears it.
        dirty: DIRTY_TYPES.has(options.type) && options.level >= CONTAMINATION_MIN_LEVEL,
        sutured: false,
        cauterized: false,
        extracted: false,
        dressedAt: 0,
        effects,
        inflictedAt: options.inflictedAt,
        healedAt: 0,
        provenance: options.provenance,
    };
}
/**
 * Write a wound onto a body: append the record, apply its effects to the part,
 * recompute the part's impairment.
 *
 * Impairment is recomputed from the wound list rather than accumulated, so
 * healing a wound gives the function back without anyone having to remember
 * what it took. That also makes the operation idempotent under replay, which
 * matters more than it sounds: a resynced client re-running a tick must not
 * cripple a limb twice.
 */
export function applyWound(body, wounds, wound) {
    const state = body.parts[wound.part];
    if (!state)
        throw new WoundTableError(`body has no part '${wound.part}'`);
    wounds.push(wound);
    const terminal = [];
    for (const effect of wound.effects) {
        switch (effect) {
            case 'sever':
                if (!state.severed)
                    terminal.push('sever');
                state.severed = true;
                break;
            case 'break':
                if (!state.broken)
                    terminal.push('break');
                state.broken = true;
                break;
            case 'blind':
                if (!state.blind)
                    terminal.push('blind');
                state.blind = true;
                break;
            case 'lodge':
            case 'kill':
            case 'knockdown':
                break;
        }
    }
    recomputeImpairment(state, wounds, wound.part);
    return {
        wound,
        fatal: wound.effects.includes('kill'),
        // AoD §5.1: level 3+ owes a check, and the table's modifier weights it.
        knockdownCheck: wound.level >= 3 || wound.effects.includes('knockdown'),
        terminal,
    };
}
/**
 * A part's soft impairment: the worst unhealed wound on it, plus a diminishing
 * share of the rest.
 *
 * Deliberately not a sum. Levels are not additive (AoD §4.2) and impairment is
 * a restatement of level, so summing it would smuggle hit points back in
 * through the side door — twenty scratches on one arm would cost the arm.
 */
export function recomputeImpairment(state, wounds, part) {
    const active = wounds
        .filter((wound) => wound.part === part && wound.state !== 'healed')
        .map((wound) => wound.impair)
        .sort((a, b) => b - a);
    let impairment = 0;
    let weight = 1;
    for (const value of active) {
        impairment += value * weight;
        weight *= 0.25;
    }
    state.impairment = clamp01(impairment);
}
/**
 * Total pain a character is carrying, after Grit (AoD §5.2).
 * `Σ max(0, wound.pain − grit)` — grit is applied per wound, so a tough
 * character shrugs off a dozen small hurts entirely and still feels the one
 * that broke their leg.
 */
export function effectivePain(wounds, gritValue) {
    let total = 0;
    for (const wound of wounds) {
        if (wound.state === 'healed')
            continue;
        total += Math.max(0, wound.pain - gritValue);
    }
    return total;
}
/**
 * Blood units per game hour across every open wound (AoD §5.3).
 *
 * A wound whose part is lost for good still bleeds: `permanent` describes what
 * will never come back, not what has stopped. A fresh severing is the heaviest
 * bleeder in the game, which is precisely why first aid is the most
 * time-critical act in it.
 */
export function totalBleedRate(wounds) {
    let total = 0;
    for (const wound of wounds) {
        if (wound.state === 'healed')
            continue;
        total += wound.bleedRate;
    }
    return total;
}
//# sourceMappingURL=wound.js.map